import { defineContract, listOf, nullableString, timestamp, z } from "./_base";

/*
 * Contracts for the Home dashboard.
 *
 * Each schema declares ONLY the fields the Home screen reads. Verified against
 * the live handlers in x92/apps/api/src/routes and the Prisma models they
 * return, not against the mockup — the Figma frame shows sample content, and
 * copying its field names into code is how a dashboard ends up unable to render
 * real data.
 */

/* ── Session ──────────────────────────────────────────────────────────────
 * GET /me  →  the AppUser row plus its provider accounts.
 */
/* The provider-account shape lives in providers.ts; /me embeds a trimmed
 * version of it, declared inline so the two files do not fight over one name. */
const meProviderSchema = z.object({
  id: z.string(),
  provider: z.string(),
  isActive: z.boolean().optional().default(false),
  lastSyncedAt: timestamp,
});

export const meSchema = z.object({
  id: z.string(),
  email: z.string().optional(),
  name: nullableString,
  providerAccounts: z.array(meProviderSchema).optional().default([]),
});
export type Me = z.infer<typeof meSchema>;

export const meContract = defineContract({
  name: "me",
  method: "GET",
  path: () => "/me",
  response: meSchema,
});

/* ── Triage ───────────────────────────────────────────────────────────────
 * GET /engagement/pending → { actions, count }, sorted by confidence desc.
 * This is the "TRIAGE — N NEED YOU" card.
 *
 * `payload` is an untyped Json column, so it is accepted as a record and read
 * defensively at the render site rather than pinned to a shape the DB does not
 * enforce.
 */
export const aiActionSchema = z.object({
  id: z.string(),
  entityId: nullableString,
  actionType: z.string(),
  status: z.string(),
  payload: z.record(z.unknown()).nullish().transform((v) => v ?? {}),
  reasoning: nullableString,
  confidence: z.number().optional().default(0.5),
  expiresAt: timestamp,
  createdAt: timestamp,
});
export type AiAction = z.infer<typeof aiActionSchema>;

export const pendingActionsContract = defineContract({
  name: "engagement.pending",
  method: "GET",
  path: () => "/engagement/pending",
  response: z.object({
    actions: z.array(aiActionSchema).optional().default([]),
    count: z.number().optional().default(0),
  }),
});

/* ── Today ────────────────────────────────────────────────────────────────
 * GET /program/today → a ProgramOfWork row. `payload` holds the blocks; the
 * column is Json, so blocks/items are validated here rather than trusted.
 */
export const programItemSchema = z.object({
  id: z.string().optional(),
  type: z.string().optional(),
  title: z.string().optional().default(""),
  status: z.string().optional().default("pending"),
  priority: z.union([z.string(), z.number()]).optional(),
  estimatedMin: z.number().optional(),
  entityId: nullableString,
  notes: nullableString,
});
export type ProgramItem = z.infer<typeof programItemSchema>;

export const programBlockSchema = z.object({
  id: z.string().optional(),
  label: z.string().optional().default(""),
  start: nullableString,
  end: nullableString,
  items: z.array(programItemSchema).optional().default([]),
});
export type ProgramBlock = z.infer<typeof programBlockSchema>;

/* The handler returns the row itself; the JSON body has lived under either
 * `payload` or `program` across migrations, so both are accepted. */
const programBodySchema = z.object({
  summary: nullableString,
  focusTheme: nullableString,
  blocks: z.array(programBlockSchema).optional().default([]),
});

export const programTodaySchema = z
  .object({
    id: z.string().optional(),
    status: z.string().optional().default("draft"),
    periodStart: timestamp,
    confirmedAt: timestamp,
    lockedAt: timestamp,
    payload: programBodySchema.nullish(),
    program: programBodySchema.nullish(),
  })
  .transform((row) => {
    const body = row.payload ?? row.program ?? { blocks: [] as ProgramBlock[] };
    return {
      id: row.id,
      status: row.status,
      periodStart: row.periodStart,
      confirmedAt: row.confirmedAt,
      lockedAt: row.lockedAt,
      summary: body.summary,
      focusTheme: body.focusTheme,
      blocks: body.blocks ?? [],
      /* Flattened for counting only. Times deliberately are NOT copied down
       * from the block onto each item: the schema puts start/end on the BLOCK
       * and items carry no time of their own, so stamping the block's time on
       * every child renders four rows all claiming "14:00", which is worse
       * than showing no time at all. The Today card groups by block instead. */
      items: (body.blocks ?? []).flatMap((b) => b.items ?? []),
    };
  });
export type ProgramToday = z.infer<typeof programTodaySchema>;

export const programTodayContract = defineContract({
  name: "program.today",
  method: "GET",
  path: () => "/program/today",
  response: programTodaySchema,
});

/* ── Signals ──────────────────────────────────────────────────────────────
 * The "SIGNALS — REVIEW QUEUES" card is four independent counts from four
 * endpoints. They are fetched separately so one slow or broken queue degrades
 * a single row instead of blanking the card.
 */
export const suggestedRelationshipsContract = defineContract({
  name: "relationships.suggested",
  method: "GET",
  path: () => "/relationships/suggested",
  response: z.object({
    suggestions: z.array(z.record(z.unknown())).optional().default([]),
    total: z.number().optional().default(0),
  }),
});

export const duplicatesContract = defineContract({
  name: "merge.duplicates",
  method: "GET",
  path: () => "/merge/duplicates",
  response: z.union([
    listOf(z.record(z.unknown())).transform((rows) => ({ groups: rows, total: rows.length })),
    z
      .object({
        duplicates: z.array(z.record(z.unknown())).optional().default([]),
        total: z.number().optional(),
      })
      .transform((v) => ({ groups: v.duplicates, total: v.total ?? v.duplicates.length })),
  ]),
});
